/**
 * Shared survey-share helpers for the Marketer View empty / nudge states.
 *
 * A brand's surveys usually share the same name, so the name doesn't help tell
 * them apart — the distinguishing detail is their cohort and country. These
 * helpers build that label, resolving cohort names from the scope's cohort list.
 */

import type { ISurveyCohort } from '../../services/nestjs-base-api';

export interface BrandSurvey {
  id?: number;
  hash: string;
  name?: string;
  status?: string;
  brandId?: number;
  brand?: { id?: number };
  countryCode?: string;
  languageCode?: string;
  cohortId?: number;
  cohort?: { id?: number; name?: string; displayName?: string };
  accessCount?: number | null;
}

/** Resolve a cohort's display name by id from the scope's cohort list. */
export function makeCohortNamer(
  cohorts: ISurveyCohort[],
): (id?: number) => string | undefined {
  const byId = new Map<number, ISurveyCohort>();
  cohorts.forEach((c) => byId.set(c.id, c));
  return (id) => {
    if (id == null) return undefined;
    const c = byId.get(id);
    return c?.displayName?.trim() || c?.name?.trim() || undefined;
  };
}

/**
 * Distinguishing label for a survey — "cohort · country". Falls back to the
 * survey name, then its id, when neither cohort nor country is available.
 */
export function surveyLabel(
  s: BrandSurvey,
  cohortName?: (id?: number) => string | undefined,
): string {
  const cohort =
    cohortName?.(s.cohortId ?? s.cohort?.id) ||
    s.cohort?.displayName?.trim() ||
    s.cohort?.name?.trim();
  const parts = [cohort, s.countryCode].filter(Boolean);
  return parts.join(' · ') || s.name?.trim() || `Survey ${s.id ?? ''}`.trim();
}
